/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiProfilesActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for AI Profiles list.
────────────────────────────────────────────────────────────────*/

import { Edit, Trash2, TestTube, Copy } from 'lucide-react';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import type { AiProfile } from '../api/aiProfilesApi';

interface AiProfilesActionsProps {
  profile: AiProfile;
  testingConnection: number | null;
  onEdit: (profile: AiProfile) => void;
  onDelete: (profile: AiProfile) => void;
  onDuplicate: (profile: AiProfile) => void;
  onTestConnection: (profile: AiProfile) => void;
}

export function AiProfilesActions({
  profile,
  testingConnection,
  onEdit,
  onDelete,
  onDuplicate,
  onTestConnection
}: AiProfilesActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-1.5">
      <SimpleTooltip content={t('aiProfiles.actions.testConnection')} position="top">
        <button
          type="button"
          onClick={() => onTestConnection(profile)}
          className="p-2 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
          aria-label={t('aiProfiles.actions.testConnection')}
          disabled={testingConnection === profile.id}
        >
          {testingConnection === profile.id ? (
            <span className="block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <TestTube className="h-4 w-4" />
          )}
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('aiProfiles.actions.edit')} position="top">
        <button
          type="button"
          onClick={() => onEdit(profile)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          aria-label={t('aiProfiles.actions.edit')}
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          type="button"
          onClick={() => onDuplicate(profile)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
          aria-label={t('common.duplicate')}
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('aiProfiles.actions.delete')} position="top">
        <button
          type="button"
          onClick={() => onDelete(profile)}
          className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          aria-label={t('aiProfiles.actions.delete')}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

